import type {
  CanalEnvio,
  ConsumoLinhaDetalhe,
  ConsumoOrigemRef,
  ConsumoOrigemTipo,
  Fatura,
  FaturaAsaasInfo,
  FaturaBreakdownItem,
  FaturaComprovanteManual,
  FaturaHistoricoAcao,
  FaturaHistoricoEntry,
  FaturaListSummary,
  FaturaOrigemPagamento,
  FaturaSegundaViaEnvio,
  FaturaStatus,
  FaturaTipoCliente,
  FaturaTipoGeracao,
  MetodoPagamento,
} from '../types';

type ApiInvoiceStatus = 'ISSUED' | 'PAID' | 'OVERDUE' | 'CANCELED' | 'REFUNDED' | 'ISSUE_ERROR';
type ApiInvoiceGenerationType = 'MANUAL' | 'BATCH' | 'SCHEDULED';
type ApiInvoiceClientType = 'CONTRACTED' | 'ONE_TIME';
type ApiPaymentOrigin = 'ASAAS' | 'MANUAL';
type ApiInvoiceHistoryAction =
  | 'CREATED'
  | 'ISSUED_AT_ASAAS'
  | 'PAYMENT_CONFIRMED'
  | 'PAYMENT_MANUAL'
  | 'RESEND_DISPATCHED'
  | 'OVERDUE_AUTO'
  | 'CANCELED'
  | 'CANCELED_BY_REOPEN'
  | 'REISSUED'
  | 'REFUNDED'
  | 'ISSUE_ERROR_AT_ASAAS'
  | 'RECEIPT_ATTACHED';
type ApiDeliveryChannel = 'EMAIL' | 'WHATSAPP';

interface ApiAsaasInfo {
  asaasId: string | null;
  link: string | null;
  method: 'PIX' | 'BOLETO' | 'CARD' | null;
  status: string | null;
  error: string | null;
  updatedAt: string | null;
}

interface ApiBreakdown {
  id: string;
  category: string;
  categoryLabel: string;
  quantity: number;
  unitValue: number | null;
  subtotal: number;
  discount: number;
}

interface ApiHistory {
  id: string;
  action: ApiInvoiceHistoryAction;
  description: string;
  details: Record<string, unknown> | null;
  userId: string | null;
  userName: string | null;
  createdAt: string;
}

interface ApiResendDispatch {
  id: string;
  channels: ApiDeliveryChannel[];
  recipientEmail: string | null;
  recipientWhatsapp: string | null;
  customMessage: string | null;
  sentById: string;
  sentByName: string;
  createdAt: string;
}

interface ApiReceipt {
  fileName: string | null;
  fileType: string | null;
  filePath: string | null;
  paidAt: string | null;
  confirmedValue: number | null;
  note: string | null;
  attachedById: string | null;
  attachedByName: string | null;
  attachedAt: string | null;
}

interface ApiConsumptionOriginRef {
  type: ConsumoOrigemTipo;
  identifier: string;
  label: string;
}

interface ApiConsumptionLine {
  id: string;
  date: string;
  category: string;
  serviceLabel: string;
  source: ApiConsumptionOriginRef;
  links: ApiConsumptionOriginRef[];
  recipientName: string | null;
  recipientDocument: string | null;
  unitValue: number;
  discount: number;
  chargedValue: number;
  note: string | null;
}

export interface ApiInvoice {
  id: string;
  number: string;
  workspaceId: string;
  tenantName: string;
  clientType: ApiInvoiceClientType;
  generationType: ApiInvoiceGenerationType;
  status: ApiInvoiceStatus;
  paymentOrigin: ApiPaymentOrigin | null;
  contractId: string | null;
  productName: string | null;
  balancePeriodId: string | null;
  balancePeriodStart: string | null;
  balancePeriodEnd: string | null;
  batchId: string | null;
  originalInvoiceId?: string | null;
  reissueId?: string | null;
  totalValue: number;
  paidValue: number | null;
  description: string;
  issuedAt: string;
  dueAt: string;
  paidAt: string | null;
  canceledAt: string | null;
  cancelReason: string | null;
  isPostalSeparateInvoice: boolean;
  generatedById: string | null;
  generatedByName: string | null;
  asaas: ApiAsaasInfo;
  consumptionStatement?: ApiConsumptionLine[];
  breakdown?: ApiBreakdown[];
  history?: ApiHistory[];
  resendDispatches?: ApiResendDispatch[];
  receipt?: ApiReceipt | null;
  receiptSignedUrl?: string | null;
  workspaceAdminContact?: {
    email: string | null;
    phone: string | null;
    name: string | null;
  } | null;
}

export interface ApiListInvoicesResponse {
  items: ApiInvoice[];
  summary: {
    totalOpen: number;
    totalPaid: number;
    totalOverdue: number;
    totalCanceled: number;
    totalValueOpen: number;
    totalValuePaid: number;
  };
}

const STATUS_MAP: Record<ApiInvoiceStatus, FaturaStatus> = {
  ISSUED: 'EMITIDA',
  PAID: 'PAGA',
  OVERDUE: 'VENCIDA',
  CANCELED: 'CANCELADA',
  REFUNDED: 'ESTORNADA',
  ISSUE_ERROR: 'ERRO_EMISSAO',
};

const GENERATION_MAP: Record<ApiInvoiceGenerationType, FaturaTipoGeracao> = {
  MANUAL: 'MANUAL',
  BATCH: 'EM_LOTE',
  SCHEDULED: 'AGENDADA',
};

const CLIENT_MAP: Record<ApiInvoiceClientType, FaturaTipoCliente> = {
  CONTRACTED: 'CONVENIADO',
  ONE_TIME: 'AVULSO',
};

const ORIGIN_MAP: Record<ApiPaymentOrigin, FaturaOrigemPagamento> = {
  ASAAS: 'ASAAS',
  MANUAL: 'MANUAL',
};

const HISTORY_ACTION_MAP: Record<ApiInvoiceHistoryAction, FaturaHistoricoAcao> = {
  CREATED: 'CRIADA',
  ISSUED_AT_ASAAS: 'EMITIDA_ASAAS',
  PAYMENT_CONFIRMED: 'PAGAMENTO_CONFIRMADO',
  PAYMENT_MANUAL: 'PAGAMENTO_MANUAL',
  RESEND_DISPATCHED: 'SEGUNDA_VIA_ENVIADA',
  OVERDUE_AUTO: 'VENCIDA_AUTOMATICA',
  CANCELED: 'CANCELADA',
  CANCELED_BY_REOPEN: 'CANCELADA_POR_REABERTURA',
  REISSUED: 'REEMITIDA',
  REFUNDED: 'ESTORNADA',
  ISSUE_ERROR_AT_ASAAS: 'ERRO_EMISSAO_ASAAS',
  RECEIPT_ATTACHED: 'COMPROVANTE_ANEXADO',
};

const PAYMENT_METHOD_MAP: Record<string, MetodoPagamento> = {
  PIX: 'PIX',
  BOLETO: 'BOLETO',
  CARD: 'CARTAO',
};

const CHANNEL_MAP: Record<ApiDeliveryChannel, CanalEnvio> = {
  EMAIL: 'EMAIL',
  WHATSAPP: 'WHATSAPP',
};

function projectAsaas(api: ApiAsaasInfo): FaturaAsaasInfo {
  return {
    asaasId: api.asaasId,
    linkPagamento: api.link,
    metodoPagamento: api.method ? (PAYMENT_METHOD_MAP[api.method] ?? null) : null,
    statusAsaas: api.status,
    erroEmissao: api.error,
    atualizadoEm: api.updatedAt,
  };
}

function projectBreakdown(items: ApiBreakdown[] | undefined): FaturaBreakdownItem[] {
  if (!items) return [];
  return items.map((b) => ({
    categoria: b.category,
    categoriaLabel: b.categoryLabel,
    quantidade: b.quantity,
    valorUnitario: b.unitValue,
    subtotal: b.subtotal,
    desconto: b.discount,
  }));
}

function projectHistory(items: ApiHistory[] | undefined): FaturaHistoricoEntry[] {
  if (!items) return [];
  return items.map((h) => ({
    id: h.id,
    acao: HISTORY_ACTION_MAP[h.action],
    usuarioId: h.userId,
    usuarioNome: h.userName,
    timestamp: h.createdAt,
    descricao: h.description,
    detalhes: h.details,
  }));
}

function projectDispatches(items: ApiResendDispatch[] | undefined): FaturaSegundaViaEnvio[] {
  if (!items) return [];
  return items.map((e) => ({
    id: e.id,
    canais: e.channels.map((c) => CHANNEL_MAP[c]),
    destinatarioEmail: e.recipientEmail,
    destinatarioWhatsapp: e.recipientWhatsapp,
    mensagemPersonalizada: e.customMessage,
    enviadoPor: e.sentByName,
    enviadoEm: e.createdAt,
  }));
}

function projectReceipt(c: ApiReceipt | null | undefined): FaturaComprovanteManual | null {
  if (!c || !c.fileName || !c.paidAt || c.confirmedValue == null) return null;
  return {
    arquivoNome: c.fileName,
    arquivoTipo: (c.fileType === 'PDF' ? 'PDF' : 'IMAGEM') as 'IMAGEM' | 'PDF',
    arquivoUrl: c.filePath ?? '',
    dataPagamento: c.paidAt,
    valorConfirmado: c.confirmedValue,
    observacao: c.note,
    anexadoPor: c.attachedByName ?? 'System',
    anexadoEm: c.attachedAt ?? '',
  };
}

function projectOrigemRef(ref: ApiConsumptionOriginRef): ConsumoOrigemRef {
  return { tipo: ref.type, identificador: ref.identifier, rotulo: ref.label };
}

function projectConsumptionLine(line: ApiConsumptionLine): ConsumoLinhaDetalhe {
  return {
    id: line.id,
    data: line.date,
    categoria: line.category,
    servicoLabel: line.serviceLabel,
    origemPrincipal: projectOrigemRef(line.source),
    vinculacoes: line.links.map(projectOrigemRef),
    destinatarioNome: line.recipientName,
    destinatarioDocumento: line.recipientDocument,
    valorUnitario: line.unitValue,
    desconto: line.discount,
    valorCobrado: line.chargedValue,
    observacao: line.note,
  };
}

export function projectApiInvoice(api: ApiInvoice): Fatura {
  return {
    id: api.id,
    numero: api.number,
    tenantId: api.workspaceId,
    tenantNome: api.tenantName,
    tipoCliente: CLIENT_MAP[api.clientType],
    contratoId: api.contractId,
    produtoNome: api.productName,
    apuracaoId: api.balancePeriodId,
    apuracaoPeriodoInicio: api.balancePeriodStart,
    apuracaoPeriodoFim: api.balancePeriodEnd,
    loteId: api.batchId,
    faturaOriginalId: api.originalInvoiceId ?? null,
    faturaReemitidaId: api.reissueId ?? null,
    status: STATUS_MAP[api.status],
    tipoGeracao: GENERATION_MAP[api.generationType],
    origemPagamento: api.paymentOrigin ? ORIGIN_MAP[api.paymentOrigin] : null,
    valorTotal: api.totalValue,
    valorPago: api.paidValue,
    descricao: api.description,
    dataEmissao: api.issuedAt,
    dataVencimento: api.dueAt,
    dataPagamento: api.paidAt,
    dataCancelamento: api.canceledAt,
    motivoCancelamento: api.cancelReason,
    asaas: projectAsaas(api.asaas),
    breakdown: projectBreakdown(api.breakdown),
    extratoConsumo: (api.consumptionStatement ?? []).map(projectConsumptionLine),
    comprovanteManual: projectReceipt(api.receipt),
    comprovanteUrl: api.receiptSignedUrl ?? null,
    adminContact: api.workspaceAdminContact
      ? {
          email: api.workspaceAdminContact.email,
          telefone: api.workspaceAdminContact.phone,
          nome: api.workspaceAdminContact.name,
        }
      : null,
    historico: projectHistory(api.history),
    enviosSegundaVia: projectDispatches(api.resendDispatches),
    geradoPor: api.generatedByName,
    ehFaturaCorreioSeparada: api.isPostalSeparateInvoice,
    criadoEm: api.issuedAt,
    atualizadoEm: api.issuedAt,
  };
}

export function projectSummary(api: ApiListInvoicesResponse['summary']): FaturaListSummary {
  return {
    totalEmitido: api.totalOpen + api.totalPaid + api.totalOverdue,
    totalPago: api.totalValuePaid,
    totalAReceber: api.totalValueOpen,
    totalVencido: api.totalOverdue,
    quantidadeComErroEmissao: 0,
  };
}
